import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { Notice } from '../components/ui';
import { t } from '../lib/i18n';

/**
 * Becoming a host is one call that adds the HOST role.
 *
 * It then refreshes the token pair, because the access token already in hand still
 * carries the old roles — the API re-reads them from the database on every request,
 * so authorization is correct either way, but the client's own `isHost` check would
 * otherwise lag by up to fifteen minutes.
 *
 * Publishing still requires an approved identity, and this page says so rather than
 * letting someone fill a whole wizard and hit a wall at the end.
 */
export default function BecomeHost() {
  const { user, isHost, refreshUser, toast, reportError } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const become = async () => {
    setBusy(true);
    try {
      await api.post('/users/me/become-host');
      await refreshUser();
      toast(t("Bienvenue chez les hôtes."), 'ok');
      navigate(user?.kycStatus === 'APPROVED' ? '/hote/annonces/nouvelle' : '/verification');
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page page--narrow stack stack--loose">
      <div className="card card--pad stack">
        <h1 style={{ fontSize: 26 }}>{t("Bienvenue chez les hôtes")}</h1>
        <p className="muted small" style={{ margin: 0 }}>
          {t("Publiez chambres, colocations ou nuitées. La publication est gratuite : Bedly prélève une commission uniquement quand une réservation est confirmée.")}
        </p>
        <ul className="small" style={{ margin: 0, paddingInlineStart: 20 }}>
          <li>{t("8 % du premier mois pour une location au mois ou en colocation")}</li>
          <li>{t("12 % par séjour confirmé pour les nuitées")}</li>
          <li>{t("Badge « Visité » après le passage de notre équipe, sous 48 h")}</li>
          <li>{t("Versements sécurisés par RIB")}</li>
        </ul>

        {isHost ? (
          <>
            <Notice tone="ok">{t("Vous êtes déjà hôte.")}</Notice>
            <Link className="btn btn--block" to="/hote">
              {t("Ouvrir le tableau de bord")}
            </Link>
          </>
        ) : (
          <>
            {user?.kycStatus !== 'APPROVED' ? (
              <Notice tone="warn">
                {t("La vérification d’identité est requise avant de publier. Vous pourrez préparer votre annonce en attendant la validation.")}
              </Notice>
            ) : null}
            <button className="btn btn--block" disabled={busy} onClick={become}>
              {busy ? t("Activation…") : t("Devenir hôte")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
